import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, FormControl, LinearProgress, MenuItem, Pagination, Paper, Select, SelectChangeEvent, Snackbar, SnackbarCloseReason, Switch, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  LinhasEsqueletoTabela,
  ListagemBuscaField,
  paperListagemSx,
  tableContainerListagemSx,
} from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce, useListagemAltura, useListagemKeyboardNav } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { IListagemContaBancaria, ContaBancariaService, TTipoContaFinanceira } from '../../../shared/services/api/cadastros/contaBancaria/ContaBancariaService';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular } from '../../../shared/components/listagem-mobile';

const OPCOES_POR_PAGINA = [10, 25, 50, 100];

const LABEL_TIPO_CONTA_FINANCEIRA: Record<TTipoContaFinanceira, string> = {
  banco: 'Banco',
  caixa: 'Caixa',
  cofre: 'Cofre',
  recebiveis: 'Recebíveis',
};

export const ListagemDeContaBancaria: React.FC = () => {
  const listaCelular = useCelular();
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const theme = useTheme();
  // No celular esconde Agência/Nº Conta/Tipo (secundárias) em vez de deixar a tabela
  // rolar na horizontal — cabe tudo na tela só com Banco/Titular/Status.
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState<IListagemContaBancaria[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'contabancaria';

  const { tabelaRef, containerHeight, rowsPerPage, setRowsPerPage } = useListagemAltura({
    cacheKey: ROWS_CACHE_KEY,
    valorInicial: Environment.LIMITE_DE_LINHAS,
    rowHeight: 48,
  });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };

  const filter = useMemo(() => {
    return searchParams.get('filter') || '';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  useEffect(() => {
    setIsLoading(true);

    debounce(() => {
      ContaBancariaService.getAll(pagina, filter, rowsPerPage)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        });
    });
  }, [pagina, filter, rowsPerPage]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ filter, pagina: '1' }, { replace: true });
    }
  }, [rowsPerPage]);

  const primeiroItem = totalCount === 0 ? 0 : (pagina - 1) * rowsPerPage + 1;
  const ultimoItem = Math.min(pagina * rowsPerPage, totalCount);
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // Sem F8 aqui (omitindo tiposBusca/onTrocarTipo): a tela tem só um campo de busca livre, sem
  // seletor de tipo — não há o que ciclar.
  const { pesquisaRef, linhaRefCallback, onBuscaKeyDown, onLinhaKeyDown } = useListagemKeyboardNav({
    rowsLength: rows.length,
    pagina,
    totalPaginas,
    onIrParaPagina: (novaPagina) => setSearchParams({ filter, pagina: novaPagina.toString() }, { replace: true }),
    onEnter: (index) => navigate(`/contabancaria/${rows[index].idconta}`),
    rowsDep: [rows],
  });

  return (
    <LayoutBaseDePagina
      titulo='Contas Financeiras'
      subtitulo='Visualize, pesquise e gerencie todas as contas financeiras cadastradas no sistema.'
      acao={
        <Button
          variant='contained'
          disableElevation
          startIcon={<AddCircleOutlineRoundedIcon />}
          onClick={() => navigate('/contabancaria/novo')}
          sx={{
            height: 44,
            borderRadius: '10px',
            px: 2.5,
            fontWeight: 600,
          }}
        >
          Adicionar
        </Button>
      }
      barraDeFerramentas={
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mx: 1, mb: 2.5 }}>
          <ListagemBuscaField
            value={filter}
            onChange={(valor) => setSearchParams({ filter: valor, pagina: '1' }, { replace: true })}
            placeholder='Pesquisar por banco, agência, conta ou titular...'
            inputRef={pesquisaRef}
            onKeyDown={onBuscaKeyDown}
          />
        </Box>
      }
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      <Paper elevation={0} sx={paperListagemSx(theme)}>
        <TableContainer
          ref={tabelaRef}
          sx={tableContainerListagemSx(containerHeight, isLoading || rows.length >= rowsPerPage)}
        >
          {/* No desktop, minWidth (não width:'100%') evita que tableLayout:'fixed'
              espreme as colunas abaixo do legível — quem rola é o TableContainer. No
              celular (smDown) Agência/Nº Conta/Tipo somem (ver abaixo), então
              width:'100%' sozinho cabe as colunas restantes na tela. */}
          <Table size='small' sx={{ width: '100%', minWidth: smDown ? 0 : 700, tableLayout: 'fixed' }}>
            {!listaCelular && (
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: smDown ? undefined : '22%' }}>Banco</TableCell>
                {!smDown && <TableCell sx={{ width: '13%' }}>Agência</TableCell>}
                {!smDown && <TableCell sx={{ width: '15%' }}>Nº Conta</TableCell>}
                <TableCell sx={{ width: smDown ? undefined : '25%' }}>Titular</TableCell>
                {!smDown && <TableCell sx={{ width: '13%' }}>Tipo</TableCell>}
                <TableCell align='center' sx={{ width: smDown ? '64px' : '12%' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            )}

            {isLoading && rows.length === 0 && (
              <TableBody>
                <LinhasEsqueletoTabela
                  colunas={smDown ? [
                    { width: '75%' },
                    { width: '80%' },
                    { variant: 'rounded', width: 34, height: 18, align: 'center' },
                  ] : [
                    { width: '75%' },
                    { width: '55%' },
                    { width: '65%' },
                    { width: '80%' },
                    { width: '55%' },
                    { variant: 'rounded', width: 34, height: 18, align: 'center' },
                  ]}
                />
              </TableBody>
            )}
            <TableBody>
              {listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idconta}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      avatar={row.nomebanco || '?'}
                      titulo={row.nomebanco}
                      detalhes={[row.agencia ? `Ag. ${row.agencia}` : null, row.numeroconta ? `C/C ${row.numeroconta}` : null, row.titular, row.tipo ? LABEL_TIPO_CONTA_FINANCEIRA[row.tipo] : null]}
                      etiqueta={{ rotulo: row.status ? 'Ativo' : 'Inativo', cor: row.status ? 'success' : 'default' }}
                      onClick={() => navigate(`/contabancaria/${row.idconta}`)}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map((row, index) => (
                <TableRow
                  key={row.idconta}
                  ref={linhaRefCallback(index)}
                  hover
                  tabIndex={0}
                  onClick={() => navigate(`/contabancaria/${row.idconta}`)}
                  onKeyDown={onLinhaKeyDown(index)}
                  sx={{
                    cursor: 'pointer',
                    outline: 'none',
                    '&:last-child td': { borderBottom: 0 },
                    '& td': {
                      py: smDown ? 0.75 : 1.25,
                      px: smDown ? 0.75 : 1.5,
                      fontSize: smDown ? '0.75rem' : '0.8125rem',
                      borderBottom: `1px solid ${theme.torq.border.default}`,
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.035),
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${theme.torq.border.focus}`,
                      outlineOffset: '-2px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <TableCell>
                    <Typography fontWeight={600} noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                      {row.nomebanco}
                    </Typography>
                  </TableCell>
                  {!smDown && (
                    <TableCell>
                      <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                        {row.agencia}
                      </Typography>
                    </TableCell>
                  )}
                  {!smDown && (
                    <TableCell>
                      <Typography fontFamily="monospace" noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                        {row.numeroconta}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                      {row.titular}
                    </Typography>
                  </TableCell>
                  {!smDown && (
                    <TableCell>
                      <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                        {row.tipo ? LABEL_TIPO_CONTA_FINANCEIRA[row.tipo] : '—'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align='center' onClick={(event) => event.stopPropagation()}>
                    <Switch size='small' checked={row.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            {rows.length === 0 && !isLoading && (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={smDown ? 3 : 6} align='center' sx={{ py: 3 }}>
                    <Box>
                      <AccountBalanceRoundedIcon sx={{
                        fontSize: 35,
                        color: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.text.secondary, 0.4)
                          : 'text.secondary',
                        mb: 1
                      }} />
                      <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                        {Environment.LISTAGEM_VAZIA}
                      </Typography>
                      <Typography variant='body2' color='text.secondary' fontSize="0.7rem">
                        {filter ? 'Tente ajustar os filtros de busca' : 'Clique em "Adicionar" para começar'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={smDown ? 3 : 6} sx={{ p: 0 }}>
                    <LinearProgress sx={{ height: '1.5px' }} />
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={smDown ? 3 : 6} sx={{ py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
                  <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
                    <Typography variant='caption' color='text.secondary'>
                      {totalCount === 0
                        ? 'Nenhuma conta encontrada'
                        : `Mostrando de ${primeiroItem} a ${ultimoItem} de ${totalCount} conta${totalCount !== 1 ? 's' : ''}`}
                    </Typography>

                    {totalCount > rowsPerPage && (
                      <Pagination
                        page={pagina}
                        count={totalPaginas}
                        onChange={(_, newPage) => setSearchParams({ filter, pagina: newPage.toString() }, { replace: true })}
                        size="small"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                      />
                    )}

                    <FormControl size='small' sx={{ minWidth: 110 }}>
                      <Select<number> value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                        {(OPCOES_POR_PAGINA.includes(rowsPerPage) ? OPCOES_POR_PAGINA : [...OPCOES_POR_PAGINA, rowsPerPage].sort((a, b) => a - b)).map((opcao) => (
                          <MenuItem key={opcao} value={opcao}>{opcao} / página</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </LayoutBaseDePagina>
  );
};
